import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import { getPlanTimeMatrix } from '../services/planService';

interface TimeMatrixRequestDialogProps {
  open: boolean;
  onClose: () => void;
  planId: number;
  onMatrixReceived?: (data: any) => void;
}

const TimeMatrixRequestDialog: React.FC<TimeMatrixRequestDialogProps> = ({
  open,
  onClose,
  planId,
  onMatrixReceived
}) => {
  const [provider, setProvider] = useState<string>('doublegis');
  const [selectedRouteTypes, setSelectedRouteTypes] = useState<string[]>(['auto', 'public_transport']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const providers = [
    { value: 'doublegis', label: '2GIS API', description: 'Real-time routing data from 2GIS' },
    { value: 'local', label: 'Local Calculation', description: 'Distance-based calculation using Haversine formula' }
  ];

  const routeTypes = [
    { value: 'auto', label: 'Auto', description: 'Car/Driving routes' },
    { value: 'public_transport', label: 'Public Transport', description: 'Bus, metro, tram routes' },
    { value: 'pedestrian', label: 'Pedestrian', description: 'Walking routes' }
  ];

  const handleRouteTypeChange = (routeType: string) => {
    setSelectedRouteTypes(prev => 
      prev.includes(routeType)
        ? prev.filter(rt => rt !== routeType)
        : [...prev, routeType]
    );
  };

      const handleRequestMatrix = async () => {
      if (selectedRouteTypes.length === 0) {
        setError('Please select at least one route type');
        return;
      }

      setLoading(true);
      setError(null);
      setResult(null);

      try {
        const routeTypesString = selectedRouteTypes.join(',');
        const data = await getPlanTimeMatrix(planId, provider, routeTypesString);
        
        setResult(data);
        if (onMatrixReceived) {
          onMatrixReceived(data);
        }
      } catch (err: any) {
        const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to request time matrix';
        
        // If 2GIS fails, suggest using local calculation
        if (provider === 'doublegis' && errorMessage.includes('2GIS')) {
          setError(`${errorMessage}. Try using 'Local Calculation' instead.`);
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

  const handleClose = () => {
    setError(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Request Time Matrix
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Calculate travel times between all locations in the plan
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3}>
          {/* Provider Selection */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Provider
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Provider</InputLabel>
              <Select
                value={provider}
                label="Select Provider"
                onChange={(e) => setProvider(e.target.value)}
              >
                {providers.map(p => (
                  <MenuItem key={p.value} value={p.value}>
                    <Box>
                      <Typography variant="body1">{p.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {p.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Route Types Selection */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Route Types
            </Typography>
            <FormGroup>
              {routeTypes.map(rt => (
                <FormControlLabel
                  key={rt.value}
                  control={
                    <Checkbox
                      checked={selectedRouteTypes.includes(rt.value)}
                      onChange={() => handleRouteTypeChange(rt.value)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{rt.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rt.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Result Display */}
          {result && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Results
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Provider:
                    </Typography>
                    <Chip label={result.provider} size="small" />
                  </Stack>
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Route Types:
                    </Typography>
                    {result.route_types?.map((rt: string) => (
                      <Chip key={rt} label={rt} size="small" />
                    ))}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Points: {result.points?.length || 0} locations
                  </Typography>
                </Box>
                {result.time_matrices && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Matrices Generated: {Object.keys(result.time_matrices).length}
                    </Typography>
                  </Box>
                )}
                {result.logs && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {result.logs.stores_without_coords?.length > 0 && 
                        `${result.logs.stores_without_coords.length} stores without coordinates`}
                      {result.logs.workers_without_coords?.length > 0 && 
                        `${result.logs.workers_without_coords.length} workers without coordinates`}
                    </Typography>
                  </Alert>
                )}
                {result.time_matrices && Object.keys(result.time_matrices).length > 0 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      ✅ Time matrices successfully generated! You can now view and edit them.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleRequestMatrix}
          variant="contained"
          disabled={loading || selectedRouteTypes.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Requesting...' : 'Request Matrix'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeMatrixRequestDialog; 